import { fromPostgrest, guard, raise, type Result } from '@/lib/result';
import { supabase } from '@/lib/supabase';

import { mapAuditEntry, type AuditEntry } from '../model/audit-entry';
import {
  mapMasjidMapItem,
  mapRequestMapItem,
  type MasjidMapItem,
  type RequestMapItem,
} from '../model/map-item';

export interface DashboardStats {
  readonly openRequests: number;
  readonly activeFreelancers: number;
  readonly pendingVerifications: number;
  readonly openTickets: number;
}

export const adminRepository = {
  async setVerified(profileId: string, verified: boolean): Promise<Result<void>> {
    return guard(async () => {
      const { error } = await supabase.rpc('set_provider_verified', {
        p_profile_id: profileId,
        p_verified: verified,
      });
      if (error) raise('server', error.message, error);
    });
  },

  async setCertifier(profileId: string, isCertifier: boolean): Promise<Result<void>> {
    return guard(async () => {
      const { error } = await supabase.rpc('set_certifier', {
        p_profile_id: profileId,
        p_is_certifier: isCertifier,
      });
      if (error) raise('server', error.message, error);
    });
  },

  async listMasjidsForMap(): Promise<Result<readonly MasjidMapItem[]>> {
    return guard(async () => {
      const { data, error } = await supabase
        .from('masjids')
        .select('id, name, city, latitude, longitude')
        .not('latitude', 'is', null)
        .not('longitude', 'is', null);
      if (error) throw fromPostgrest(error);
      return ((data ?? []) as unknown[]).map(mapMasjidMapItem);
    });
  },

  async listRequestsForMap(): Promise<Result<readonly RequestMapItem[]>> {
    return guard(async () => {
      const { data, error } = await supabase
        .from('coverage_requests')
        .select(
          'id, status, masjid_id, start_date, end_date, masjids!inner(name, latitude, longitude)',
        )
        .not('masjids.latitude', 'is', null)
        .not('masjids.longitude', 'is', null);
      if (error) throw fromPostgrest(error);
      return ((data ?? []) as unknown[]).map(mapRequestMapItem);
    });
  },

  /** Phase 7 (D-015): dashboard stat cards. Four parallel `head:true` counts —
   * RLS admin-only for tickets/verifications; profiles + coverage_requests are
   * app-wide readable so the counts are exact. */
  async dashboardStats(): Promise<Result<DashboardStats>> {
    return guard(async () => {
      const [openReq, freelancers, pendingCreds, openTix] = await Promise.all([
        supabase
          .from('coverage_requests')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'open'),
        supabase
          .from('profiles')
          .select('id', { count: 'exact', head: true })
          .eq('is_freelance', true),
        supabase
          .from('credential_documents')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'pending'),
        supabase
          .from('support_tickets')
          .select('id', { count: 'exact', head: true })
          .in('status', ['open', 'in_progress']),
      ]);
      for (const r of [openReq, freelancers, pendingCreds, openTix]) {
        if (r.error) throw fromPostgrest(r.error);
      }
      return {
        openRequests: openReq.count ?? 0,
        activeFreelancers: freelancers.count ?? 0,
        pendingVerifications: pendingCreds.count ?? 0,
        openTickets: openTix.count ?? 0,
      };
    });
  },

  /** Phase 7: audit-log surface (Phase 4 audit_log table, RLS admin-only). */
  async listAuditLog(limit = 200): Promise<Result<readonly AuditEntry[]>> {
    return guard(async () => {
      const { data, error } = await supabase
        .from('audit_log')
        .select(
          'id, actor_id, action, target_type, target_id, detail, created_at, actor:profiles!actor_id(full_name)',
        )
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw fromPostgrest(error);
      return ((data ?? []) as unknown[]).map(mapAuditEntry);
    });
  },
};
